'use client';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { usePathname } from 'next/navigation';
import { useLangCurrency } from '@/contexts/LangCurrencyContext';
import { useRoleAuth, UserRole } from '@/contexts/RoleAuthContext';
import { MENU_ITEMS } from './navItems';

interface MobileMenuDrawerProps {
  menuOpen: boolean;
  onCloseMenu: () => void;
}

const ROLE_BADGE_COLORS: Record<UserRole, string> = {
  owner: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  manager: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  appraiser: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
};

/** Full slide-in menu: all modules + an always-visible account footer. */
export default function MobileMenuDrawer({ menuOpen, onCloseMenu }: MobileMenuDrawerProps) {
  const { lang, currency, toggleLang, toggleCurrency } = useLangCurrency();
  const { roleUser, roleLogout } = useRoleAuth();
  const activePath = usePathname();
  const router = useRouter();

  if (!menuOpen) return null;

  const handleLogout = async () => {
    onCloseMenu();
    await roleLogout();
    router.push('/login');
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onCloseMenu} />
      <aside className="absolute left-0 top-0 bottom-0 w-80 max-w-[88vw] bg-card border-r border-border flex flex-col shadow-2xl">
        {/* Header: account identity */}
        <div className="flex items-center gap-2 p-4 border-b border-border">
          {roleUser && (
            <>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {roleUser.avatar}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{roleUser.name}</p>
                <p className="text-xs text-muted-foreground truncate">{roleUser.email}</p>
              </div>
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize flex-shrink-0 ${ROLE_BADGE_COLORS[roleUser.role]}`}
              >
                {roleUser.role}
              </span>
            </>
          )}
        </div>

        {/* Modules — scrollable */}
        <div className="flex-1 overflow-y-auto p-3 pb-4">
          <nav className="space-y-1">
            {MENU_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={onCloseMenu}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  activePath === item.href
                    ? 'bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={item.icon as never} size={18} className="flex-shrink-0" />
                <span className="truncate">{lang === 'EN' ? item.label : item.labelTh}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Account footer — language/currency, site link, sign out. Always visible. */}
        <div className="border-t border-border p-3 space-y-1">
          <button
            onClick={toggleLang}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-surface transition-colors"
          >
            <Icon name="GlobeAltIcon" size={16} className="text-blue-400 flex-shrink-0" />
            <span className="flex-1 text-left">Language</span>
            <span className="text-xs text-muted-foreground">
              {lang === 'EN' ? 'English' : 'ไทย'}
            </span>
          </button>
          <button
            onClick={toggleCurrency}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-surface transition-colors"
          >
            <Icon name="CurrencyDollarIcon" size={16} className="text-violet-400 flex-shrink-0" />
            <span className="flex-1 text-left">Currency</span>
            <span className="text-xs text-muted-foreground">{currency}</span>
          </button>
          <hr className="my-2 border-border/60" />
          <Link
            href="/landing"
            onClick={onCloseMenu}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-surface transition-colors"
          >
            <Icon name="GlobeAltIcon" size={16} className="text-sky-400 flex-shrink-0" />
            <span className="flex-1 text-left">Back to Site</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Icon name="ArrowRightOnRectangleIcon" size={16} className="flex-shrink-0" />
            <span className="flex-1 text-left">Sign Out</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
